import React, { useState, useEffect } from 'react';
import { IconSearch, IconCheck, IconX, IconEye } from '../components/Icons';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const CATEGORY_EMOJI = { Grocery: '🛒', Food: '🍱', Clothes: '👗' };

function Stars({ rating }) {
  if (!rating) return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>No ratings</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ color: '#F59E0B', fontSize: 13 }}>★</span>
      <span style={{ fontWeight: 700, fontSize: 13 }}>{rating}</span>
    </div>
  );
}

export default function Shops() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [catFilter, setCatFilter] = useState('All');
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch shops from Firestore (now fetching from users collection where role is shopkeeper)
  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('joinDate', 'desc'));
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.role === 'shopkeeper');
      setShops(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Delete demo shops (status === 'Pending')
  const deleteDemoData = async () => {
    const demoDocs = shops.filter(s => s.status === 'Pending');
    for (const s of demoDocs) {
      await deleteDoc(doc(db, 'users', s.id));
    }
    alert('Demo shops deleted');
  };

  const changePassword = async (id, current) => {
    const newPass = window.prompt("नवीन पासवर्ड टाका (Enter New Password):", current);
    if (newPass && newPass !== current) {
      try {
        await updateDoc(doc(db, 'users', id), { adminPassword: newPass });
        alert("पासवर्ड बदलला आहे!");
      } catch (e) {
        alert("Error: " + e.message);
      }
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const shop = shops.find(s => s.id === id);
      const updateData = { status };
      
      if (status === 'Approved' || status === 'Active') {
        updateData.isApproved = true;
        updateData.isShopOpen = true; // Auto-open on first approval
        updateData.shopkeeperApproved = true;
      } else if (status === 'Blocked') {
        updateData.isApproved = false;
        updateData.isShopOpen = false;
      }
      
      // Generate admin password if approving for the first time
      if ((status === 'Approved' || status === 'Active') && !shop.adminPassword) {
        updateData.adminPassword = Math.floor(100000 + Math.random() * 900000).toString();
      }
      await updateDoc(doc(db, 'users', id), updateData);
    } catch (e) {
      alert("Error updating status: " + e.message);
    }
  };

  const [selectedShop, setSelectedShop] = useState(null);

  const filtered = shops.filter(s => {
    const searchStr = search.toLowerCase();
    const shopName = (s.shopName || s.name || '').toLowerCase();
    const ownerName = (s.name || s.owner || '').toLowerCase();
    const phone = (s.phone || '');
    const altPhone = (s.alternatePhone || '');
    const upi = (s.upiId || '').toLowerCase();

    const matchSearch = shopName.includes(searchStr) || 
                       ownerName.includes(searchStr) || 
                       phone.includes(searchStr) ||
                       altPhone.includes(searchStr) ||
                       upi.includes(searchStr);
                       
    const matchFilter = filter === 'All' || s.status === filter || (filter === 'Approved' && s.status === 'Active');
    const matchCat = catFilter === 'All' || s.category === catFilter;
    return matchSearch && matchFilter && matchCat;
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
          <IconSearch size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            placeholder="दुकान किंवा मालकाचे नाव, फोन, UPI शोधा | Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['All', 'Approved', 'Pending', 'Blocked'].map(f => (
            <button
              key={f}
              className={`btn ${filter === f ? 'btn-primary' : 'btn-secondary'} btn-sm`}
              onClick={() => setFilter(f)}
            >
              {f === 'Pending' ? `⏳ ${f} (${shops.filter(s => s.status === 'Pending').length})` : f}
            </button>
          ))}
        </div>
        {/* Delete demo data button */}
        <button className="btn btn-danger btn-sm" onClick={deleteDemoData}>Delete Demo Data</button>
      </div>

      {/* Category filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['All', 'Grocery', 'Food', 'Clothes'].map(c => (
          <button
            key={c}
            onClick={() => setCatFilter(c)}
            style={{
              padding: '6px 14px', borderRadius: 99, fontSize: 12, fontWeight: 500,
              cursor: 'pointer', border: 'none', transition: 'all 0.2s',
              background: catFilter === c ? 'var(--primary)' : 'var(--bg-card)',
              color: catFilter === c ? 'white' : 'var(--text-secondary)',
              boxShadow: catFilter === c ? '0 0 12px rgba(255,107,53,0.3)' : 'none'
            }}
          >
            {CATEGORY_EMOJI[c] || '🏪'} {c}
          </button>
        ))}
      </div>

      {/* Shop cards grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading shops...</div>
        ) : (
          filtered.map(s => (
            <div key={s.id} className="card" style={{
              borderColor: s.status === 'Pending' ? 'var(--warning)' :
                s.status === 'Blocked' ? 'var(--danger)' : 'var(--border)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10, fontSize: 22,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--bg)'
                }}>{CATEGORY_EMOJI[s.category] || '🏪'}</div>
                <span className={`badge ${s.status === 'Approved' || s.status === 'Active' ? 'success' : s.status === 'Pending' ? 'warning' : 'danger'}`}>
                  {s.status === 'Active' ? 'Approved' : s.status}
                </span>
              </div>
              
              <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--primary)', marginBottom: 2 }}>
                🏪 {s.shopName || s.name || 'Shop Name'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                👤 <strong>मालक:</strong> {s.name || s.owner || 'N/A'} • 📞 {s.phone || 'N/A'}
              </div>
              {s.timing && (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  ⏰ <strong>वेळ:</strong> {s.timing}
                </div>
              )}
              {s.upiId && (
                <div style={{ fontSize: 11, color: '#10B981', fontWeight: 600, marginBottom: 6 }}>
                  💳 <strong>UPI:</strong> {s.upiId}
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                📍 {s.village || s.address || 'Sangola'}
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${s.shopName || s.name} ${s.village || 'Sangola'}`)}`}
                  target="_blank"
                  rel="noreferrer"
                  title="View on Google Maps"
                  style={{ 
                    color: 'var(--primary)', textDecoration: 'none', 
                    fontSize: 10, display: 'flex', alignItems: 'center',
                    background: 'var(--bg)', padding: '2px 6px', borderRadius: 4,
                    border: '1px solid var(--border)'
                  }}
                >
                  मॅप | Map
                </a>
              </div>
              
              {(s.status === 'Approved' || s.status === 'Active') && s.adminPassword && (
                <div style={{ padding: '8px 12px', background: 'rgba(59, 130, 246, 0.1)', border: '1px dashed var(--info)', borderRadius: 8, fontSize: 13, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>Login Password:</span>
                    <span style={{ color: 'var(--info)', fontWeight: '800', letterSpacing: 2 }}>{s.adminPassword}</span>
                  </div>
                  <button 
                    onClick={() => changePassword(s.id, s.adminPassword)}
                    className="btn btn-secondary btn-sm"
                    style={{ padding: '4px 8px', fontSize: 10 }}
                  >
                    📝 Edit
                  </button>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
                {[
                  { l: 'Orders', v: s.orders || 0 },
                  { l: 'Revenue', v: `₹${s.revenue || 0}` },
                  { l: 'Rating', v: <Stars rating={s.rating} /> },
                ].map((d, i) => (
                  <div key={i} style={{ background: 'var(--bg)', padding: '8px 10px', borderRadius: 7, textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{d.l}</div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{d.v}</div>
                  </div>
                ))}
              </div>

              {(s.status === 'Approved' || s.status === 'Active') && (s.revenue > 0) && (
                <button 
                  onClick={async () => {
                    if (window.confirm(`तुम्ही ${s.shopName || s.name} चा ₹${s.revenue} हिशोब पूर्ण (Settle) करू इच्छिता?`)) {
                      try {
                        await updateDoc(doc(db, 'users', s.id), { revenue: 0 });
                        alert("हिशोब पूर्ण झाला!");
                      } catch (e) {
                        alert("Error: " + e.message);
                      }
                    }
                  }}
                  className="btn btn-secondary btn-sm"
                  style={{ width: '100%', marginBottom: 10, borderColor: 'var(--success)', color: 'var(--success)', background: 'rgba(16, 185, 129, 0.1)' }}
                >
                  💰 हिशोब पूर्ण करा (Settle)
                </button>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                {s.status === 'Pending' && (
                  <>
                    <button onClick={() => updateStatus(s.id, 'Active')} className="btn btn-success btn-sm" style={{ flex: 1, justifyContent: 'center' }}><IconCheck size={12} /> Approve</button>
                    <button onClick={() => updateStatus(s.id, 'Rejected')} className="btn btn-danger btn-sm" style={{ flex: 1, justifyContent: 'center' }}><IconX size={12} /> Reject</button>
                  </>
                )}
                {(s.status === 'Approved' || s.status === 'Active') && (
                  <>
                    <button onClick={() => setSelectedShop(s)} className="btn btn-secondary btn-sm" style={{ flex: 1, justifyContent: 'center' }}><IconEye size={12} /> View</button>
                    <button onClick={() => updateStatus(s.id, 'Blocked')} className="btn btn-danger btn-sm" style={{ flex: 1, justifyContent: 'center' }}>Block</button>
                  </>
                )}
                {s.status === 'Blocked' && (
                  <button onClick={() => updateStatus(s.id, 'Active')} className="btn btn-success btn-sm w-full" style={{ justifyContent: 'center' }}><IconCheck size={12} /> Unblock Shop</button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Shop Details Modal */}
      {selectedShop && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20
        }}>
          <div className="card" style={{ maxWidth: 500, width: '100%', background: 'var(--bg-card)', borderRadius: 16, padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: 'var(--primary)' }}>🏪 {selectedShop.shopName || selectedShop.name} Details</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setSelectedShop(null)}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16, fontSize: 13 }}>
              <div><strong>दुकानाचे नाव:</strong> {selectedShop.shopName || selectedShop.name || 'N/A'}</div>
              <div><strong>मालकाचे नाव:</strong> {selectedShop.name || selectedShop.owner || 'N/A'}</div>
              <div><strong>मोबाईल नंबर:</strong> {selectedShop.phone || 'N/A'}</div>
              <div><strong>व्हॉट्सॲप / दुसरा नंबर:</strong> {selectedShop.alternatePhone || 'N/A'}</div>
              <div><strong>दुकानाची वेळ:</strong> {selectedShop.timing || '9 AM - 9 PM'}</div>
              <div><strong>कॅटेगरी:</strong> {selectedShop.category || 'General'}</div>
              <div style={{ gridColumn: 'span 2' }}><strong>UPI ID (पेआऊट):</strong> {selectedShop.upiId || 'Not Added'}</div>
              <div style={{ gridColumn: 'span 2' }}><strong>पत्ता:</strong> {selectedShop.village || selectedShop.address || 'Sangola'}</div>
            </div>

            <button className="btn btn-primary w-full" onClick={() => setSelectedShop(null)}>बंद करा (Close)</button>
          </div>
        </div>
      )}
    </div>
  );
}
